import { createRef, FC, memo, useCallback, useMemo, useState } from 'react';
import { DecimalFormat } from '@tempusfinance/decimal';
import { ButtonWrapper } from '@tempusfinance/common-ui';
import { Nullable } from '../../../interfaces';
import BaseInput from '../BaseInput';
import Typography, { TypographyColor } from '../Typography';
import Logo from '../Logo';
import Icon from '../Icon';
import ButtonTertiary from '../ButtonTertiary';
import LoadingPlaceholder from '../LoadingPlaceholder';
import TokenSelector from './TokenSelector';

import './CurrencyInput.scss';
import NetworkSelector from './NetworkSelector';
import { Decimal } from '../../../datastructures';
import { CurrencyInputToken, CurrencyInputNetwork } from './CurrencyInputTypes';

export type { CurrencyInputToken, CurrencyInputNetwork };

const INPUT_PREVIEW_DIGITS = 6;

export interface CurrencyInputProps {
  tokenPrecision: Nullable<number>;
  uiTokenPrecision: Nullable<number>;
  tokens: CurrencyInputToken[];
  selectedToken: CurrencyInputToken;
  amount: string;
  showAmount?: boolean;
  maxAmount?: Decimal | null;
  maxAmountLabel?: string;
  tokenRate: Nullable<Decimal>;
  error?: JSX.Element | string;
  autoFocus?: boolean;
  editable?: boolean;
  showValuePercentage?: boolean;
  percentageValueOverride?: Nullable<Decimal>;
  title?: string;
  tokenSearch?: boolean;
  collapseTokenList?: boolean;
  reserveGas?: boolean;
  selectedNetwork?: Nullable<CurrencyInputNetwork>;
  networks?: CurrencyInputNetwork[];
  networkSelectorTitle?: string;
  disabled?: boolean;
  onUpdate?: (value: string) => void;
  onTokenSelected?: (token: CurrencyInputToken) => void;
  onNetworkSelected?: (network: CurrencyInputNetwork) => void;
  onDebounceUpdate?: (value: string) => void;
}

const CurrencyInputComponent: FC<CurrencyInputProps> = props => {
  const {
    tokenPrecision,
    uiTokenPrecision,
    tokens,
    selectedToken,
    maxAmount,
    amount,
    showAmount,
    tokenRate,
    error,
    autoFocus,
    maxAmountLabel = '',
    editable = true,
    showValuePercentage = true,
    percentageValueOverride = null,
    title = '',
    tokenSearch = true,
    collapseTokenList = false,
    reserveGas = false,
    selectedNetwork,
    networks,
    networkSelectorTitle = '',
    disabled = false,
    onUpdate,
    onTokenSelected,
    onNetworkSelected,
    onDebounceUpdate,
  } = props;
  const inputRef = createRef<HTMLInputElement>();

  const [focused, setFocused] = useState<boolean>(false);
  const [tokenSelectorOpen, setTokenSelectorOpen] = useState<boolean>(false);
  const [networkSelectorOpen, setNetworkSelectorOpen] = useState<boolean>(false);

  const percentageValue = useMemo(() => {
    if (!maxAmount || maxAmount.equals(Decimal.ZERO) || !tokenPrecision) {
      return null;
    }

    // We want to make sure `amount` that user enters and `max amount` have same precision
    const maxAmountTruncated = maxAmount.toTruncated(tokenPrecision, false);

    return Decimal.parse(amount, Decimal.ZERO).div(maxAmountTruncated);
  }, [amount, tokenPrecision, maxAmount]);

  const percentageValueFormatted = useMemo(() => {
    if (percentageValueOverride) {
      return DecimalFormat.format(percentageValueOverride, { style: 'percentage', fractionDigits: 2 });
    }

    if (percentageValue) {
      return DecimalFormat.format(percentageValue, { style: 'percentage', fractionDigits: 2 });
    }

    return null;
  }, [percentageValue, percentageValueOverride]);

  const percentageValueColor = useMemo((): TypographyColor => {
    if (percentageValueOverride) {
      if (percentageValueOverride.equals(0)) {
        return 'text-supporting';
      }
      return percentageValueOverride.gt(0) ? 'text-success' : 'text-error';
    }

    return 'text-supporting';
  }, [percentageValueOverride]);

  const formattedMaxAmount = useMemo(
    () =>
      maxAmount && uiTokenPrecision && selectedToken
        ? DecimalFormat.format(maxAmount, {
            style: 'currency',
            fractionDigits: uiTokenPrecision,
            currency: selectedToken.symbol,
            lessThanFormat: true,
          })
        : '',
    [selectedToken, uiTokenPrecision, maxAmount],
  );

  const usdAmount = useMemo(() => {
    const decimalAmount = Decimal.parse(amount, Decimal.ZERO);
    if (decimalAmount.isZero()) {
      return '$0.00';
    }

    if (tokenRate) {
      const usdAmount = Decimal.parse(amount, Decimal.ZERO).mul(tokenRate);
      const formattedUsdAmount = DecimalFormat.format(usdAmount, {
        style: 'currency',
        fractionDigits: 2,
        currency: '$',
        lessThanFormat: true,
      });

      if (usdAmount.isZero() || formattedUsdAmount.startsWith('<')) {
        return formattedUsdAmount;
      } else {
        return `~${formattedUsdAmount}`;
      }
    }
  }, [amount, tokenRate]);

  const handleValueChange = useCallback(
    (value: string) => {
      onUpdate?.(value);
    },
    [onUpdate],
  );

  const handleDebounceValueChange = useCallback(
    (value: string) => {
      onDebounceUpdate?.(value);
    },
    [onDebounceUpdate],
  );

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const handleInputFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => setFocused(false), []);

  const handlePercentageClick = useCallback(
    (percentage: Decimal) => {
      if (!maxAmount || !tokenPrecision) {
        return;
      }

      let effectiveMaxAmount = maxAmount;
      if (reserveGas) {
        if (selectedToken.symbol === 'ETH') {
          effectiveMaxAmount = Decimal.max(effectiveMaxAmount.sub(0.001), Decimal.ZERO);
        } else if (selectedToken.symbol === 'STRK') {
          effectiveMaxAmount = Decimal.max(effectiveMaxAmount.sub(10), Decimal.ZERO);
        }
      }

      if (percentage.equals(1)) {
        handleValueChange(effectiveMaxAmount.toString());
        handleDebounceValueChange(effectiveMaxAmount.toString());
      } else {
        const value = Decimal.min(percentage.mul(maxAmount), effectiveMaxAmount);
        const formattedValue = value.toTruncated(tokenPrecision, false);

        handleValueChange(formattedValue);
        handleDebounceValueChange(formattedValue);
      }
    },
    [maxAmount, tokenPrecision, reserveGas, selectedToken.symbol, handleValueChange, handleDebounceValueChange],
  );

  const handleQuarterClick = useCallback(() => {
    handlePercentageClick(new Decimal(0.25));
  }, [handlePercentageClick]);

  const handleHalfClick = useCallback(() => {
    handlePercentageClick(new Decimal(0.5));
  }, [handlePercentageClick]);

  const handleMaxAmountLabelClick = useCallback(() => {
    handlePercentageClick(new Decimal(1));
  }, [handlePercentageClick]);

  const onOpenTokenSelector = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setTokenSelectorOpen(true);
  }, []);

  const onCloseTokenSelector = useCallback(() => {
    setTokenSelectorOpen(false);
  }, []);

  const onNetworkSelectorClose = useCallback(() => {
    setNetworkSelectorOpen(false);
  }, []);

  const onNetworkSelectorOpen = useCallback(() => {
    setNetworkSelectorOpen(true);
  }, []);

  const handleTokenSelected = useCallback(
    (token: CurrencyInputToken) => {
      onTokenSelected?.(token);
      onCloseTokenSelector();
    },
    [onCloseTokenSelector, onTokenSelected],
  );

  const handleNetworkSelected = useCallback(
    (network: CurrencyInputNetwork) => {
      onNetworkSelected?.(network);
      onNetworkSelectorClose();
    },
    [onNetworkSelectorClose, onNetworkSelected],
  );

  const dataLoaded =
    (Boolean(maxAmount) || !showAmount) &&
    Boolean(tokenPrecision) &&
    Boolean(uiTokenPrecision) &&
    Boolean(selectedToken);

  const amountDecimal = useMemo(() => {
    return Decimal.parse(amount, Decimal.ZERO);
  }, [amount]);

  const displayAmount = useMemo(() => {
    if (focused) {
      return amount;
    }

    const original = amountDecimal.toString();
    const truncated = amountDecimal.toTruncated(INPUT_PREVIEW_DIGITS);

    return original === truncated ? original : `${truncated}...`;
  }, [amount, amountDecimal, focused]);

  return (
    <div className="nostra__currencyInput" data-disabled={disabled}>
      <div className="nostra__currencyInput__header">
        <div className="nostra__currencyInput__headerTitle">
          {title && (
            <Typography variant="body-md" color="text-primary">
              {title}
            </Typography>
          )}
          {selectedNetwork && (
            <div className="nostra__currencyInput__bridge" onClick={onNetworkSelectorOpen}>
              <img src={selectedNetwork.logoUrl} className="nostra__currencyInput__bridgeNetworkLogo" />
              <Typography variant="input-ticker" weight="semi-bold">
                {selectedNetwork.displayName}
              </Typography>
              <Icon variant="down-chevron" size={16} color="--icon-default" />
            </div>
          )}
        </div>
        {showAmount && (
          <>
            {dataLoaded ? (
              <div className="nostra__currencyInput__maxAmount">
                {maxAmountLabel && (
                  <Typography
                    className="nostra__currencyInput__maxAmountLabel"
                    variant="body-sm"
                    color="text-supporting"
                  >
                    {maxAmountLabel}
                  </Typography>
                )}
                {formattedMaxAmount && (
                  <Typography variant="body-sm" color="text-primary">
                    {formattedMaxAmount}
                  </Typography>
                )}
                {editable && (
                  <div className="nostra__currencyInput__percentageButtons">
                    <ButtonTertiary onClick={handleQuarterClick}>
                      <Typography color="text-primary" variant="button-xs" weight="semi-bold">
                        25%
                      </Typography>
                    </ButtonTertiary>
                    <ButtonTertiary onClick={handleHalfClick}>
                      <Typography color="text-primary" variant="button-xs" weight="semi-bold">
                        50%
                      </Typography>
                    </ButtonTertiary>
                    <ButtonTertiary onClick={handleMaxAmountLabelClick}>
                      <Typography color="text-primary" variant="button-xs" weight="semi-bold">
                        Max
                      </Typography>
                    </ButtonTertiary>
                  </div>
                )}
              </div>
            ) : (
              <LoadingPlaceholder width="140px" height="14px" />
            )}
          </>
        )}
      </div>
      <div
        className={`nostra__currencyInput__fieldContainer
          ${focused ? ' nostra__currencyInput__fieldContainerFocused' : ''}
          ${error ? ' nostra__currencyInput__fieldContainerError' : ''}
        `}
      >
        <div className="nostra__currencyInput__inputContainer" onClick={focusInput}>
          {dataLoaded && selectedToken ? (
            <ButtonWrapper onClick={onOpenTokenSelector} disabled={tokens.length < 2}>
              <div className="nostra__currencyInput__token">
                {selectedToken.logoUri && (
                  <img
                    src={selectedToken.logoUri}
                    width={24}
                    height={24}
                    onError={e => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                {selectedToken.logoType && <Logo type={selectedToken.logoType} size={24} />}
                <Typography className="nostra__currencyInput__tokenTicker" variant="input-ticker">
                  {selectedToken.symbol}
                </Typography>
                {tokens.length > 1 && <Icon variant="down-chevron" size={18} color="--icon-default" />}
              </div>
            </ButtonWrapper>
          ) : (
            <LoadingPlaceholder width="90px" height="42px" />
          )}
          <div className="nostra__currencyInput__amountContainer">
            {dataLoaded ? (
              <Typography
                className="nostra__currencyInput__amount"
                variant="input-amount"
                color="text-primary"
                weight="semi-bold"
              >
                <BaseInput
                  ref={inputRef}
                  value={displayAmount}
                  placeholder="0"
                  pattern={`[0-9]*[.]?[0-9]{0,${tokenPrecision}}`}
                  disabled={!editable}
                  debounce
                  autoFocus={dataLoaded && autoFocus}
                  onChange={handleValueChange}
                  onDebounceChange={handleDebounceValueChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </Typography>
            ) : (
              <LoadingPlaceholder width="50px" height="38px" />
            )}
            {dataLoaded && usdAmount && (
              <div className="nostra__currencyInput__fiatAmount">
                <Typography variant="input-body-sm" color="text-supporting">
                  {usdAmount}
                </Typography>
                {percentageValueFormatted && showValuePercentage && (
                  <Typography variant="input-body-sm" color={percentageValueColor}>
                    &nbsp;({percentageValueFormatted})
                  </Typography>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {error && (
        <Typography className="nostra__currencyInput__error" variant="body-sm" color="text-error">
          {error}
        </Typography>
      )}
      <TokenSelector
        open={tokenSelectorOpen}
        tokens={tokens}
        tokenSearch={tokenSearch}
        collapseTokenList={collapseTokenList}
        onClose={onCloseTokenSelector}
        onTokenSelected={handleTokenSelected}
      />
      <NetworkSelector
        open={networkSelectorOpen}
        networks={networks || []}
        onClose={onNetworkSelectorClose}
        onNetworkSelected={handleNetworkSelected}
        title={networkSelectorTitle}
        selectedToken={selectedToken}
      />
    </div>
  );
};

export const CurrencyInput = memo(CurrencyInputComponent);
